/**
 * Play definitions shared across Betting, About, Store, etc.
 * User-facing copy: always use {@link getAugmentDisplayName} / {@link getAugmentMeta} (never hardcode labels in UI).
 * `displayName` on each def is the single source of truth; `cardLabel` is kept equal for legacy fields.
 * Asset paths: {@link ./augmentAssets.js} (`/augments/{match|clash|round}/{slug}/…`).
 */

import {
  AUGMENT_KIND,
  augmentCodeToAssetSlug,
  getAugmentDefaultSvgPath,
  getAugmentKind,
  getAugmentStyleSvgPath,
  ROUND_NONE_SVG
} from './augmentAssets';
import {
  STORE_CATALOG,
  STYLE_ID,
  GOLD_STYLE_ID,
  WINNER_ICON_CODES,
  getStoreStyleCost,
  getStoreStyleName,
  DEFAULT_STORE_STYLE_NAME,
  sortStyleIdsByReleaseDesc,
  getStoreOfferings
} from 'arena-shared';
import {
  HOURGLASS_POINTS,
  HOURGLASS_MOVE_COST,
  HOURGLASS_MIN_POINTS,
  HOURGLASS_AUGMENT_CODE,
  isHourglassPlacementOpenForGame,
  resolveHourglassPoints,
  canMoveHourglass,
  nextHourglassPoints
} from 'arena-shared/hourglassAugment';
import {
  BOUNTY_AUGMENT_CODE,
  BOUNTY_STAKE_MIN,
  BOUNTY_STAKE_MAX,
  isValidBountyStake,
  resolveBountyPoints,
  clampBountyStake
} from 'arena-shared/bountyAugment';
import { clashWinnerIconSrc, formatClashWinnerIcon } from './clashWinnerIcon';

export {
  getStoreOfferings,
  STORE_ROTATION_MS,
  STORE_WINNER_OFFERING_COUNT,
  STORE_AUGMENT_OFFERING_COUNT,
  STORE_OFFERING_COUNT
} from 'arena-shared';
export {
  HOURGLASS_POINTS,
  HOURGLASS_MOVE_COST,
  HOURGLASS_MIN_POINTS,
  HOURGLASS_AUGMENT_CODE,
  isHourglassPlacementOpenForGame,
  resolveHourglassPoints,
  canMoveHourglass,
  nextHourglassPoints
};
export {
  BOUNTY_AUGMENT_CODE,
  BOUNTY_STAKE_MIN,
  BOUNTY_STAKE_MAX,
  isValidBountyStake,
  resolveBountyPoints,
  clampBountyStake
};

export {
  AUGMENT_ASSET_ROOT,
  AUGMENT_KIND,
  augmentCodeToAssetSlug,
  getAugmentDefaultSvgPath,
  getAugmentKind,
  getAugmentStyleSvgPath,
  ROUND_NONE_SVG
} from './augmentAssets';

/** Max tactical points per round for domestic leagues (WC uses dynamic cap). */
export const TACTICAL_POINTS_PER_ROUND = 12;

export function tacticalPointsCapForMatchCount(matchCount) {
  const n = Number(matchCount);
  if (!Number.isFinite(n) || n < 1) return TACTICAL_POINTS_PER_ROUND;
  return Math.floor(n * 1.5);
}

/** Match + round play TP cap for a betting round. */
export function getTacticalPointsCapForRound(leagueCode, round) {
  if (leagueCode === 'WC' && round?.games?.length) {
    const open = round.games.filter((g) => isGameOnRoundCard(g));
    return tacticalPointsCapForMatchCount(open.length || round.games.length);
  }
  return TACTICAL_POINTS_PER_ROUND;
}

/** Ensure `displayName` is set; sync legacy `cardLabel` / `label` fields. */
export function normalizeAugmentDef(def) {
  if (!def) return def;
  const displayName = def.displayName ?? def.cardLabel ?? def.label ?? def.fullLabel ?? def.value ?? '';
  return {
    ...def,
    displayName,
    cardLabel: displayName,
    label: def.label ?? displayName
  };
}

/** Clash 1v1 augments: max TP per opponent (clash key), not pooled across all clashes (keep in sync with lambdas clashAugments.js). */
export const CLASH_TACTICAL_POINTS_MAX_PER_CLASH = 6;

export const CLASH_AUGMENT_TACTICAL_COST = {
  sa: 3,
  bs: 1,
  eif: 2,
  gd: 2
};

const CLASH_AUGMENT_DEFS = [
  {
    value: 'sa',
    displayName: 'Steal',
    shortDesc: 'Take their match points on this game for this league',
    desc: 'On this match vs your clash opponent, you take all of their match points for that league-they score 0 on this game in the weekly clash and those points transfer into that league\'s standings (you gain them; they lose them there). Does not change your main-card match points, public round tables, or other leagues. Blocked if they play Block steal on the same match. When both players steal, you swap match points for that game instead.',
    color: 'gold',
    svg: getAugmentDefaultSvgPath('sa', AUGMENT_KIND.CLASH)
  },
  {
    value: 'bs',
    displayName: 'Block steal',
    shortDesc: 'Blocks one incoming steal on this game',
    desc: 'On this match, blocks one incoming Steal from your clash opponent. Does not stop Ahead or Tip the Scales. Each type can be used once per opponent per round; clash plays use a separate tactical budget from your main card.',
    color: 'cyan',
    svg: getAugmentDefaultSvgPath('bs', AUGMENT_KIND.CLASH)
  },
  {
    value: 'eif',
    displayName: 'Ahead',
    shortDesc: '+8 in this league if you finish ahead on this game',
    desc: 'After Steal / Block / Tip the Scales on this match, if your match points are higher than your clash opponent\'s, gain +8 on this game for the weekly clash and that league\'s standings. Ties score no bonus. Does not change your main-card match points or other leagues.',
    color: 'green',
    svg: getAugmentDefaultSvgPath('eif', AUGMENT_KIND.CLASH)
  },
  {
    value: 'gd',
    displayName: 'Tip the Scales',
    shortDesc: 'Split both players\' points on this game equally',
    desc: 'After Steal / Block resolution on this match, pool both players\' clash points for the game and give each floor of half (odd leftover discarded). Then Ahead is evaluated. Does not change main-card match points or other leagues. Block does not stop Scales. Costs 2 clash tactical points.',
    color: 'gold',
    svg: getAugmentDefaultSvgPath('gd', AUGMENT_KIND.CLASH)
  }
];

export const CLASH_AUGMENT_OPTIONS = CLASH_AUGMENT_DEFS.map((def) =>
  normalizeAugmentDef({
    ...def,
    label: def.displayName,
    tacticalPointsCost: CLASH_AUGMENT_TACTICAL_COST[def.value] ?? 0
  })
);

/** @returns {(typeof CLASH_AUGMENT_OPTIONS)[number] | null} */
export function getClashAugmentOption(code) {
  if (!code || typeof code !== 'string') return null;
  const { code: base } = parseAugmentRef(code);
  return CLASH_AUGMENT_OPTIONS.find((o) => o.value === base) ?? null;
}

export function getClashAugmentTacticalPointsCost(code) {
  if (!code || typeof code !== 'string') return 0;
  const { code: base } = parseAugmentRef(code);
  return CLASH_AUGMENT_TACTICAL_COST[base] ?? 0;
}

/** Tactical points cost per play when placed on a match (keep in sync with lambdas/src/utils/augmentTacticalPoints.js) */
export const AUGMENT_TACTICAL_POINTS_COST = {
  '2x': 2,
  g: 2,
  '+1h': 2,
  '+1a': 2,
  gd: 2,
  s: 2,
  p: 2,
  d: 2,
  m: 0,
  b: 0,
  h: 2,
  rch: 1,
  rca: 1,
  hp: 1,
  ap: 1,
  mph: 1,
  mpa: 1,
  et: 2,
  ph: 2,
  pa: 2
};

/** Multiplicative factor on consensus multiplier for sacrifice beneficiary (`m`) — 70% increase. */
export const SACRIFICE_MULTIPLIER_FACTOR = 1.7;

/** Parlay (`p`): flat bonus per match when both linked 1×2 outcomes are correct. */
export const PARLAY_POINTS = 6;
export const PARLAY_AUGMENT_CODE = 'p';
export const PARLAY_MAX_USES = 2;

/** Split augment: second-line weight must be in this inclusive range. */
export const SPLIT_SECOND_WEIGHT_MIN = 1;
export const SPLIT_SECOND_WEIGHT_MAX = 5;
export const SPLIT_TOTAL_WEIGHT = 10;

/** Knockout-only match plays (KO stages; not in store). */
export const KNOCKOUT_MATCH_AUGMENT_CODES = ['et', 'ph', 'pa'];

export const SHOOTOUT_UI_VALUE = 'shootout';
export const SHOOTOUT_VARIANTS = ['ph', 'pa'];

/**
 * Parse play reference:
 * - code or code#styleId (e.g. 2x#1)
 * - optional targets after a second #: code#styleId#0,2,3 or code##0,2,3 (round slots)
 * - optional split payload: code#styleId#H-A,W or code##H-A,W (match split augment)
 * @param {string} ref
 * @returns {{ code: string, styleId: number|null, targets: number[], split: { home: number, away: number, weight: number }|null }}
 */
export function parseAugmentRef(ref) {
  if (!ref || typeof ref !== 'string') {
    return { code: '', styleId: null, targets: [], split: null };
  }

  const hashCount = (ref.match(/#/g) || []).length;
  if (hashCount >= 2) {
    const first = ref.indexOf('#');
    const second = ref.indexOf('#', first + 1);
    const code = ref.slice(0, first);
    const stylePart = ref.slice(first + 1, second);
    const targetsPart = ref.slice(second + 1);

    let styleId = null;
    if (stylePart !== '') {
      if (!/^\d+$/.test(stylePart)) {
        return { code: ref, styleId: null, targets: [], split: null };
      }
      const n = parseInt(stylePart, 10);
      if (!Number.isInteger(n) || n <= 0) {
        return { code: ref, styleId: null, targets: [], split: null };
      }
      styleId = n;
    }

    if (targetsPart === '') return { code, styleId, targets: [], split: null };

    const splitMatch = targetsPart.match(/^(\d+)-(\d+),(\d+)$/);
    if (splitMatch) {
      const home = parseInt(splitMatch[1], 10);
      const away = parseInt(splitMatch[2], 10);
      const weight = parseInt(splitMatch[3], 10);
      return { code, styleId, targets: [], split: { home, away, weight } };
    }

    if (!/^\d+(,\d+)*$/.test(targetsPart)) {
      return { code: ref, styleId: null, targets: [], split: null };
    }
    const targets = targetsPart.split(',').map((s) => parseInt(s, 10));
    return { code, styleId, targets, split: null };
  }

  const idx = ref.lastIndexOf('#');
  if (idx <= 0) return { code: ref, styleId: null, targets: [], split: null };
  const suffix = ref.slice(idx + 1);
  if (!/^\d+$/.test(suffix)) return { code: ref, styleId: null, targets: [], split: null };
  const styleId = parseInt(suffix, 10);
  if (!Number.isInteger(styleId) || styleId <= 0) {
    return { code: ref, styleId: null, targets: [], split: null };
  }
  return { code: ref.slice(0, idx), styleId, targets: [], split: null };
}

/**
 * @param {string} code
 * @param {number|null|undefined} styleId
 * @param {number[]|null|undefined} targets
 */
export function formatAugmentRef(code, styleId, targets) {
  if (!code) return '';
  const slots = Array.isArray(targets)
    ? targets.filter((n) => Number.isInteger(n) && n >= 0)
    : [];
  const hasStyle = styleId != null && styleId !== 0;
  if (slots.length === 0) {
    if (!hasStyle) return code;
    return `${code}#${styleId}`;
  }
  const styleSeg = hasStyle ? String(styleId) : '';
  return `${code}#${styleSeg}#${slots.join(',')}`;
}

/**
 * When Sacrifice is placed on a match, drop that match from votes and from AON/SN slots.
 * @param {{ votes?: string[]|null, roundAugment?: string|null, gameId: string, gameSlot: number|null|undefined }} args
 * @returns {{ votes: string[], roundAugment: string|null }}
 */
export function clearRoundSelectionsForSacrificedMatch({ votes, roundAugment, gameId, gameSlot }) {
  const gid = String(gameId);
  const nextVotes = Array.isArray(votes) ? votes.map(String).filter((id) => id !== gid) : [];
  let nextRoundAugment = roundAugment == null || roundAugment === '' ? null : String(roundAugment);
  if (nextRoundAugment && gameSlot != null && Number.isInteger(gameSlot)) {
    const parsed = parseAugmentRef(nextRoundAugment);
    if ((parsed.code === 'aon' || parsed.code === 'sn') && parsed.targets.includes(gameSlot)) {
      const nextTargets = parsed.targets.filter((s) => s !== gameSlot);
      nextRoundAugment = formatAugmentRef(parsed.code, parsed.styleId, nextTargets);
    }
  }
  return { votes: nextVotes, roundAugment: nextRoundAugment };
}

/** Remove Sacrifice (`d`) / Multiplier (`m`) / Bounty (`b`) plays from a bets map. */
export function stripSacrificeAugmentsFromBets(bets) {
  const next = { ...(bets || {}) };
  for (const gid of Object.keys(next)) {
    const aug = next[gid]?.augment;
    if (!aug) continue;
    const code = getAugmentBaseCode(aug);
    if (code !== 'd' && code !== 'm' && code !== BOUNTY_AUGMENT_CODE) continue;
    const b = { ...next[gid] };
    delete b.augment;
    delete b.bountyPoints;
    next[gid] = b;
  }
  return next;
}

/** Remove Multiplier / Bounty follow-ups; keep Sacrifice (`d`). */
export function stripSacrificeFollowUpFromBets(bets) {
  const next = { ...(bets || {}) };
  for (const gid of Object.keys(next)) {
    const aug = next[gid]?.augment;
    if (!aug) continue;
    const code = getAugmentBaseCode(aug);
    if (code !== 'm' && code !== BOUNTY_AUGMENT_CODE) continue;
    const b = { ...next[gid] };
    delete b.augment;
    delete b.bountyPoints;
    next[gid] = b;
  }
  return next;
}

/** Remove Parlay (`p`) plays from a bets map. */
export function stripParlayAugmentsFromBets(bets) {
  const next = { ...(bets || {}) };
  for (const gid of Object.keys(next)) {
    const aug = next[gid]?.augment;
    if (!aug) continue;
    if (getAugmentBaseCode(aug) !== PARLAY_AUGMENT_CODE) continue;
    const b = { ...next[gid] };
    delete b.augment;
    next[gid] = b;
  }
  return next;
}

/** @returns {{ gameId: string, parsed: ReturnType<typeof parseAugmentRef>, augment: string } | null} */
export function findParlayAnchorEntry(bets) {
  for (const [gid, bet] of Object.entries(bets || {})) {
    if (!bet?.augment) continue;
    const parsed = parseAugmentRef(String(bet.augment));
    if (parsed.code === PARLAY_AUGMENT_CODE && parsed.targets.length === 1) {
      return { gameId: String(gid), parsed, augment: String(bet.augment) };
    }
  }
  return null;
}

/** Count of Parlay placements in a bets map. */
export function countParlayPlacements(bets) {
  let n = 0;
  for (const bet of Object.values(bets || {})) {
    if (bet?.augment && getAugmentBaseCode(bet.augment) === PARLAY_AUGMENT_CODE) n += 1;
  }
  return n;
}

/** True when two `p` refs form a complete linked pair. */
export function isParlayPairComplete(bets, gameIdToSlot = gameSlotFromGameId) {
  const entries = [];
  for (const [gid, bet] of Object.entries(bets || {})) {
    if (!bet?.augment) continue;
    const parsed = parseAugmentRef(String(bet.augment));
    if (parsed.code !== PARLAY_AUGMENT_CODE) continue;
    entries.push({ gameId: String(gid), parsed, slot: gameIdToSlot(gid) });
  }
  if (entries.length !== PARLAY_MAX_USES) return false;
  const withTarget = entries.filter((e) => e.parsed.targets.length === 1);
  const withoutTarget = entries.filter((e) => e.parsed.targets.length === 0);
  if (withTarget.length !== 1 || withoutTarget.length !== 1) return false;
  const anchor = withTarget[0];
  const partner = withoutTarget[0];
  return anchor.parsed.targets[0] === partner.slot && anchor.slot !== partner.slot;
}

/** @returns {{ gameId: string, parsed: ReturnType<typeof parseAugmentRef>, augment: string } | null} */
export function findSacrificeDeadEntry(bets) {
  for (const [gid, bet] of Object.entries(bets || {})) {
    if (!bet?.augment) continue;
    const parsed = parseAugmentRef(String(bet.augment));
    if (parsed.code === 'd') return { gameId: String(gid), parsed, augment: String(bet.augment) };
  }
  return null;
}

/** True when d is linked to exactly one Multiplier or Bounty on the target slot. */
export function isSacrificePairComplete(bets, gameIdToSlot = gameSlotFromGameId) {
  const dead = findSacrificeDeadEntry(bets);
  if (!dead || dead.parsed.targets.length !== 1) return false;
  const targetSlot = dead.parsed.targets[0];
  const followUps = [];
  for (const [gid, bet] of Object.entries(bets || {})) {
    if (!bet?.augment) continue;
    const code = getAugmentBaseCode(bet.augment);
    if (code !== 'm' && code !== BOUNTY_AUGMENT_CODE) continue;
    followUps.push({ gameId: String(gid), code });
  }
  if (followUps.length !== 1) return false;
  return gameIdToSlot(followUps[0].gameId) === targetSlot;
}

/** Stake currently on a Bounty bet in this map (0 if none). */
export function bountyStakeFromBets(bets) {
  for (const bet of Object.values(bets || {})) {
    if (!bet?.augment) continue;
    if (getAugmentBaseCode(bet.augment) !== BOUNTY_AUGMENT_CODE) continue;
    return resolveBountyPoints(bet.bountyPoints);
  }
  return 0;
}

/** @returns {{ gameId: string, parsed: ReturnType<typeof parseAugmentRef>, augment: string } | null} */
export function findBountyEntry(bets) {
  for (const [gid, bet] of Object.entries(bets || {})) {
    if (!bet?.augment) continue;
    const parsed = parseAugmentRef(String(bet.augment));
    if (parsed.code === BOUNTY_AUGMENT_CODE) {
      return { gameId: String(gid), parsed, augment: String(bet.augment) };
    }
  }
  return null;
}

/** @returns {{ gameId: string, augment: string, hourglassPoints: number } | null} */
export function findHourglassEntry(bets) {
  for (const [gid, bet] of Object.entries(bets || {})) {
    if (!bet?.augment) continue;
    if (getAugmentBaseCode(String(bet.augment)) === HOURGLASS_AUGMENT_CODE) {
      return {
        gameId: String(gid),
        augment: String(bet.augment),
        hourglassPoints: resolveHourglassPoints(bet.hourglassPoints)
      };
    }
  }
  return null;
}

function gameSlotFromGameId(gameId) {
  const s = String(gameId || '');
  const idx = s.lastIndexOf('#');
  if (idx < 0) return null;
  const n = Number(s.slice(idx + 1));
  return Number.isInteger(n) && n >= 0 ? n : null;
}


/**
 * @param {string} code
 * @param {number|null|undefined} styleId
 * @param {{ home: number, away: number, weight: number }} split
 */
export function formatSplitAugmentRef(code, styleId, split) {
  if (!code || !split) return '';
  const home = Number(split.home);
  const away = Number(split.away);
  const weight = Number(split.weight);
  if (!Number.isInteger(home) || home < 0) return '';
  if (!Number.isInteger(away) || away < 0) return '';
  if (!Number.isInteger(weight) || weight < 0) return '';
  const hasStyle = styleId != null && styleId !== 0;
  const styleSeg = hasStyle ? String(styleId) : '';
  return `${code}#${styleSeg}#${home}-${away},${weight}`;
}

export function isValidSplitPayload(split) {
  if (!split || typeof split !== 'object') return false;
  const { home, away, weight } = split;
  if (!Number.isInteger(home) || home < 0) return false;
  if (!Number.isInteger(away) || away < 0) return false;
  if (
    !Number.isInteger(weight) ||
    weight < SPLIT_SECOND_WEIGHT_MIN ||
    weight > SPLIT_SECOND_WEIGHT_MAX
  ) {
    return false;
  }
  return true;
}

/** 1×2 outcome for a scoreline (`home` | `draw` | `away`). */
export function scorelineOutcome(home, away) {
  const h = Number(home);
  const a = Number(away);
  if (!Number.isInteger(h) || !Number.isInteger(a) || h < 0 || a < 0) return null;
  if (h > a) return 'home';
  if (a > h) return 'away';
  return 'draw';
}

/**
 * Split second line must use a different scoreline than the primary
 * (same 1×2 is allowed — e.g. 2-1 and 1-0).
 * @param {number} primaryHome
 * @param {number} primaryAway
 * @param {{ home: number, away: number, weight?: number }|null|undefined} split
 */
export function splitHasDistinctScoreline(primaryHome, primaryAway, split) {
  if (!split || typeof split !== 'object') return false;
  const ph = Number(primaryHome);
  const pa = Number(primaryAway);
  const sh = Number(split.home);
  const sa = Number(split.away);
  if (![ph, pa, sh, sa].every((n) => Number.isInteger(n) && n >= 0)) return false;
  return ph !== sh || pa !== sa;
}

/**
 * True when primary and secondary use different 1×2 outcomes (optional hedge).
 * @param {number} primaryHome
 * @param {number} primaryAway
 * @param {{ home: number, away: number, weight?: number }|null|undefined} split
 */
export function splitHasDistinctOutcome(primaryHome, primaryAway, split) {
  if (!split || typeof split !== 'object') return false;
  const primary = scorelineOutcome(primaryHome, primaryAway);
  const secondary = scorelineOutcome(split.home, split.away);
  return primary != null && secondary != null && primary !== secondary;
}

/** Default second line when placing Split (weight 5). Prefers same 1×2, different scoreline. */
export function defaultSplitForPrimary(primaryHome, primaryAway) {
  const ph = Number(primaryHome) || 0;
  const pa = Number(primaryAway) || 0;
  if (ph > pa) {
    if (ph === 1 && pa === 0) return { home: 2, away: 0, weight: 5 };
    return { home: 1, away: 0, weight: 5 };
  }
  if (pa > ph) {
    if (ph === 0 && pa === 1) return { home: 0, away: 2, weight: 5 };
    return { home: 0, away: 1, weight: 5 };
  }
  if (ph === 0 && pa === 0) return { home: 1, away: 1, weight: 5 };
  return { home: 0, away: 0, weight: 5 };
}

/**
 * Ensure `s` refs always carry a valid split payload with a different scoreline.
 * @param {string} ref
 * @param {number} primaryHome
 * @param {number} primaryAway
 */
export function ensureSplitAugmentRef(ref, primaryHome, primaryAway) {
  if (!ref) return ref;
  const parsed = parseAugmentRef(String(ref));
  if (parsed.code !== 's') return ref;
  const ph = Number(primaryHome);
  const pa = Number(primaryAway);
  let split = parsed.split;
  if (!isValidSplitPayload(split)) {
    split = defaultSplitForPrimary(ph, pa);
  } else if (
    Number.isInteger(ph) &&
    Number.isInteger(pa) &&
    !splitHasDistinctScoreline(ph, pa, split)
  ) {
    const next = defaultSplitForPrimary(ph, pa);
    split = { home: next.home, away: next.away, weight: split.weight };
  }
  return formatSplitAugmentRef('s', parsed.styleId, split);
}

export function getAugmentBaseCode(ref) {
  return parseAugmentRef(ref).code;
}

/** Retired match augments: not in pool/store; scoring still applies on saved bets. */
export const RETIRED_MATCH_AUGMENT_CODES = new Set(['rc', 'gd']);

export function isRetiredMatchAugment(codeOrRef) {
  if (!codeOrRef) return false;
  return RETIRED_MATCH_AUGMENT_CODES.has(getAugmentBaseCode(String(codeOrRef)));
}

const LEGACY_MATCH_AUGMENT_META = {
  rc: normalizeAugmentDef({
    value: 'rc',
    displayName: 'Red Bonus',
    shortDesc: '+2 if any red card (retired)',
    color: 'red',
    svg: getAugmentDefaultSvgPath('red-card', AUGMENT_KIND.MATCH)
  }),
  gd: normalizeAugmentDef({
    value: 'gd',
    displayName: 'Tip the Scales',
    shortDesc: 'Boost precision bands (retired match play)',
    color: 'gold',
    svg: getAugmentDefaultSvgPath('gd', AUGMENT_KIND.CLASH)
  })
};

export function getAugmentTacticalPointsCost(value) {
  if (!value || typeof value !== 'string') return 0;
  const { code } = parseAugmentRef(value);
  return AUGMENT_TACTICAL_POINTS_COST[code] ?? 0;
}

/** @deprecated Use {@link augmentCodeToAssetSlug} */
export function augmentCodeToStyleFileSlug(code) {
  return augmentCodeToAssetSlug(code);
}

/** @deprecated Use {@link getAugmentStyleSvgPath} */
export function getStyleSvgPath(augmentCode, styleId) {
  return getAugmentStyleSvgPath(augmentCode, styleId);
}

/** Round-level plays (gid "round" in bets table); same TP as lambdas augmentTacticalPoints.js */
export const ROUND_AUGMENT_TACTICAL_POINTS_COST = {
  os: 3,
  aon: 3,
  gc: 2,
  bus: 3,
  sn: 2,
  bl: 3
};

export const DEFAULT_ROUND_AUGMENTS = [
  {
    value: 'os',
    label: 'Sweep aside',
    fullLabel: 'Sweep aside',
    cardLabel: 'Sweep aside',
    shortDesc: 'Correct 1X2 with predicted and actual GD of 2+ → +5 each',
    desc:
      'Each time you predict a win by two or more goals, get the 1X2 right, and the match also finishes as a win by two or more, you earn +5 bonus points. Draws and one-goal wins do not pay. You do not need the exact scoreline. Settles when the round closes.',
    tacticalPointsCost: ROUND_AUGMENT_TACTICAL_POINTS_COST.os,
    color: 'violet',
    svg: getAugmentDefaultSvgPath('os', AUGMENT_KIND.ROUND)
  },
  {
    value: 'aon',
    label: 'All or nothing',
    fullLabel: 'All or nothing',
    cardLabel: 'All / nothing',
    shortDesc: 'Up to 7 matches: all correct → 1/2/4/…/64 pts, else 0',
    desc:
      'Pick up to seven matches you have bet on. If you get the 1X2 outcome right on every selected match, you earn a single bonus based on how many you picked: 1 → 1 pt, 2 → 2, 3 → 4, 4 → 8, 5 → 16, 6 → 32, 7 → 64. Miss even one selected outcome and you get 0. Exact-score match points still work as usual.\n\n' +
      'Fewer than seven is fine. Settles when the round closes.',
    tacticalPointsCost: ROUND_AUGMENT_TACTICAL_POINTS_COST.aon,
    color: 'red',
    svg: getAugmentDefaultSvgPath('aon', AUGMENT_KIND.ROUND)
  },
  {
    value: 'gc',
    label: 'Goals carnival',
    fullLabel: 'Goals carnival',
    cardLabel: 'Goals +/−3',
    shortDesc: 'Round goal total: exact 30 · ±1 → 20 · ±2 → 10 · ±3 → 5',
    desc: 'Add up total goals in your predictions across every finished match you picked, and compare that sum to the actual total across those same games. Exact match earns +30 bonus points, within ±1 earns +20, within ±2 earns +10, within ±3 earns +5. Settles when the round closes.',
    tacticalPointsCost: ROUND_AUGMENT_TACTICAL_POINTS_COST.gc,
    color: 'cyan',
    svg: getAugmentDefaultSvgPath('gc', AUGMENT_KIND.ROUND)
  },
  {
    value: 'bus',
    label: 'Park the bus',
    fullLabel: 'Park the bus',
    cardLabel: 'Park the bus',
    shortDesc: '+5 per correct zero-goal side',
    desc:
      'Each time you correctly predict a team will score zero (home or away), you earn +5 bonus points. You do not need the exact full scoreline; only the zero you called must match. Settles when the round closes.',
    tacticalPointsCost: ROUND_AUGMENT_TACTICAL_POINTS_COST.bus,
    color: 'green',
    svg: getAugmentDefaultSvgPath('bus', AUGMENT_KIND.ROUND)
  },
  {
    value: 'sn',
    label: 'Safety net',
    fullLabel: 'Safety net',
    cardLabel: 'Safety net',
    shortDesc: 'Up to 5 matches: wrong outcomes pay 5→4→3→2→1',
    desc:
      'Pick up to five matches you have bet on. For each selected match where you miss the 1X2 outcome, you earn a bonus when the round closes — but each extra miss pays less: 1st miss +5, 2nd +4, 3rd +3, 4th +2, 5th +1 (max 15 if all five miss). Correct outcomes on those matches give no Safety-net points. Exact-score match points still work as usual.\n\n' +
      'Fewer than five is fine. Settles when the round closes.',
    tacticalPointsCost: ROUND_AUGMENT_TACTICAL_POINTS_COST.sn,
    color: 'gold',
    svg: getAugmentDefaultSvgPath('sn', AUGMENT_KIND.ROUND)
  },
  {
    value: 'bl',
    label: 'Bad luck',
    fullLabel: 'Bad luck protection',
    cardLabel: 'Bad luck',
    shortDesc: 'Near miss (wrong 1X2, off by 1 goal) → +5/+6/+7/+8 (max 4)',
    desc:
      'Across every finished match you picked, each near miss earns progressive bonus points: wrong 1X2 outcome and your scoreline is exactly one goal away from the actual (home and away combined). First near miss +5, second +6, third +7, fourth +8 (max 26). Further near misses pay nothing. Correct outcomes — even one goal off — do not count. Settles when the round closes.',
    tacticalPointsCost: ROUND_AUGMENT_TACTICAL_POINTS_COST.bl,
    color: 'lime',
    svg: getAugmentDefaultSvgPath('bl', AUGMENT_KIND.ROUND)
  }
];

/** All / nothing bonus by selected slot count (1→1, 2→2, 3→4, …, 7→64). */
export const ALL_OR_NOTHING_SLOT_POINTS = [1, 2, 4, 8, 16, 32, 64];

/** Max match slots All / nothing can select in one round. */
export const ALL_OR_NOTHING_MAX_SLOTS = ALL_OR_NOTHING_SLOT_POINTS.length;

/** Max match slots Safety net can protect in one round. */
export const SAFETY_NET_MAX_SLOTS = 5;

/** Progressive Safety net payout by miss count (1st…5th wrong outcome). */
export const SAFETY_NET_POINTS_BY_MISS = [5, 4, 3, 2, 1];

/** Total Safety net points for a given wrong-outcome count. */
export function safetyNetPointsForWrongCount(wrongCount) {
  const n = Number(wrongCount);
  if (!Number.isInteger(n) || n <= 0) return 0;
  return SAFETY_NET_POINTS_BY_MISS.slice(0, Math.min(n, SAFETY_NET_POINTS_BY_MISS.length)).reduce(
    (sum, p) => sum + p,
    0
  );
}

/** Progressive Bad luck protection payout by near-miss count (1st…4th). */
export const BAD_LUCK_PROTECTION_POINTS_BY_HIT = [5, 6, 7, 8];

/** Max near misses Bad luck protection pays for in one round. */
export const BAD_LUCK_PROTECTION_MAX_HITS = BAD_LUCK_PROTECTION_POINTS_BY_HIT.length;

/** Total Bad luck protection points for a given near-miss count. */
export function badLuckProtectionPointsForNearMissCount(nearMissCount) {
  const n = Number(nearMissCount);
  if (!Number.isInteger(n) || n <= 0) return 0;
  return BAD_LUCK_PROTECTION_POINTS_BY_HIT.slice(
    0,
    Math.min(n, BAD_LUCK_PROTECTION_MAX_HITS)
  ).reduce((sum, p) => sum + p, 0);
}

/** Points All / nothing pays if every selected slot is correct (0 if none selected). */
export function allOrNothingPointsForSlotCount(slotCount) {
  const n = Number(slotCount);
  if (!Number.isInteger(n) || n <= 0 || n > ALL_OR_NOTHING_MAX_SLOTS) return 0;
  return ALL_OR_NOTHING_SLOT_POINTS[n - 1];
}

export function getRoundAugmentTacticalPointsCost(value) {
  if (!value || typeof value !== 'string') return 0;
  const { code } = parseAugmentRef(value);
  return ROUND_AUGMENT_TACTICAL_POINTS_COST[code] ?? 0;
}

export function totalMatchAndRoundTacticalPoints(round, roundAugment = undefined, now = Date.now(), leagueCode) {
  const ra = roundAugment !== undefined ? roundAugment : round?.roundAugment;
  const capLeague = leagueCode ?? round?.leagueCode;
  const tp = totalTacticalPointsForOpenGamesBets(round, now) + getRoundAugmentTacticalPointsCost(ra);
  return tp;
}

function attachTacticalPointsCost(list) {
  return list.map((a) =>
    normalizeAugmentDef({
      ...a,
      tacticalPointsCost: AUGMENT_TACTICAL_POINTS_COST[a.value] ?? 0
    })
  );
}

function createTeamSideUiAugment({
  value,
  displayName,
  shortDesc,
  desc,
  aboutDesc,
  color,
  svg,
  tacticalPointsCost
}) {
  return normalizeAugmentDef({
    value,
    displayName,
    shortDesc,
    desc,
    aboutDesc,
    color,
    svg,
    tacticalPointsCost
  });
}

const DEFAULT_AUGMENT_DEFS = [
  {
    value: '2x',
    label: 'DoB',
    fullLabel: 'Double or bust',
    cardLabel: 'Double or bust',
    shortDesc: 'Force 2.0× rarity (2.1× if you voted & elected); miss −10×(2.0−R)',
    desc:
      'Force this match\'s outcome rarity multiplier to 2.0× (2.1× if you voted for it and it was community-elected). Precision bonuses are not multiplied. If you miss the 1×2, you lose 10 × (2.0 − community rarity tier) points (no vote in that formula). 2 tactical points. Only one play per match.',
    color: 'blue',
    svg: getAugmentDefaultSvgPath('2x', AUGMENT_KIND.MATCH)
  },
  {
    value: 'g',
    label: '+1G',
    fullLabel: 'One Goal',
    cardLabel: 'One Goal',
    shortDesc: 'One goal off: wrong 1×2 → flat 10; correct 1×2 near miss → exact 20',
    desc:
      'Drop on a match. If your scoreline is exactly one goal off in total: wrong 1×2 pays a flat 10 points (not rarity-multiplied); correct 1×2 upgrades your one-goal-off pick to exact-score points (20, rarity still scales the outcome part). Does nothing if you are farther than one goal off, or if you already nailed the exact score. Once per round.',
    color: 'violet',
    svg: getAugmentDefaultSvgPath('g', AUGMENT_KIND.MATCH)
  },
  {
    value: 'rch',
    label: 'RCH',
    fullLabel: 'Red Card Home',
    cardLabel: 'Red Bonus Home',
    shortDesc: '+5 points if the home team gets a red card',
    desc: '+5 bonus points on top of normal match scoring if the home team receives a red card (any home player sent off).',
    color: 'red',
    svg: getAugmentDefaultSvgPath('rch', AUGMENT_KIND.MATCH)
  },
  {
    value: 'rca',
    label: 'RCA',
    fullLabel: 'Red Card Away',
    cardLabel: 'Red Bonus Away',
    shortDesc: '+5 points if the away team gets a red card',
    desc: '+5 bonus points on top of normal match scoring if the away team receives a red card (any away player sent off).',
    color: 'red',
    svg: getAugmentDefaultSvgPath('rca', AUGMENT_KIND.MATCH)
  },
  {
    value: 'hp',
    label: 'HP',
    fullLabel: 'Home Penalty',
    cardLabel: 'Home PK',
    shortDesc: '+4 if home scores a successful penalty',
    desc: '+4 bonus points on top of normal match scoring if the home team scores from the penalty spot.',
    color: 'cyan',
    svg: getAugmentDefaultSvgPath('hp', AUGMENT_KIND.MATCH)
  },
  {
    value: 'ap',
    label: 'AP',
    fullLabel: 'Away Penalty',
    cardLabel: 'Away PK',
    shortDesc: '+4 if away scores a successful penalty',
    desc: '+4 bonus points on top of normal match scoring if the away team scores from the penalty spot.',
    color: 'cyan',
    svg: getAugmentDefaultSvgPath('ap', AUGMENT_KIND.MATCH)
  },
  {
    value: 'mph',
    label: 'MPH',
    fullLabel: 'Home Missed Penalty',
    cardLabel: 'Home Missed PK',
    shortDesc: '+8 points if home misses a penalty',
    desc: '+8 bonus points on top of normal match scoring if the home team misses a penalty (saved, off the post, over the bar, etc.).',
    color: 'orange',
    svg: getAugmentDefaultSvgPath('mph', AUGMENT_KIND.MATCH)
  },
  {
    value: 'mpa',
    label: 'MPA',
    fullLabel: 'Away Missed Penalty',
    cardLabel: 'Away Missed PK',
    shortDesc: '+8 points if away misses a penalty',
    desc: '+8 bonus points on top of normal match scoring if the away team misses a penalty (saved, off the post, over the bar, etc.).',
    color: 'orange',
    svg: getAugmentDefaultSvgPath('mpa', AUGMENT_KIND.MATCH)
  },
  {
    value: 's',
    label: 'SPL',
    fullLabel: 'Split',
    cardLabel: 'Split',
    shortDesc: 'Second scoreline at 1–5; better line × rarity multiplier',
    desc: 'Add a second scoreline (must differ from your primary, e.g. not 0-0 and 0-0; same 1×2 is fine). Put 1–5 weight on it (primary keeps 10 minus that). Each line scores the normal precision ladder, then floor-scales by its share. You keep only the better line — including when both have the right outcome but one is closer — then the rarity multiplier scales the outcome share only (precision share stays flat). Both scorelines count toward lock-time community outcome/result pick stats.',
    color: 'blue',
    svg: getAugmentDefaultSvgPath('s', AUGMENT_KIND.MATCH)
  },
  {
    value: 'p',
    label: 'PLY',
    fullLabel: 'Parlay',
    cardLabel: 'Parlay',
    shortDesc: 'Link two matches — +6 each if both 1×2 correct',
    desc: 'Drag onto two matches to link them (2 TP for the pair). If both finish with the correct 1×2, each match gets a flat +6 bonus (not multiplied). Incomplete pairs are rejected on submit.',
    color: 'gold',
    svg: getAugmentDefaultSvgPath('p', AUGMENT_KIND.MATCH)
  },
  {
    value: 'h',
    label: 'HG',
    fullLabel: 'Hourglass',
    cardLabel: 'Hourglass',
    shortDesc: '+8 if 1×2 correct — after lock each hop −2 (down to +2)',
    desc: 'Drag onto any match before its second half starts. +8 flat bonus when you get the 1×2 right at full time (not multiplied). After the round locks, each move to another game costs 2 of that bonus (8→6→4→2); at +2 it stays put. Locks for good once second half kicks off on that match. Costs 2 tactical points.',
    color: 'lime',
    svg: getAugmentDefaultSvgPath('h', AUGMENT_KIND.MATCH)
  },
  {
    value: 'd',
    label: 'SAC',
    fullLabel: 'Sacrifice',
    cardLabel: 'Sacrifice',
    shortDesc: 'Void this match (0 pts), then Multiplier or Bounty on another',
    desc: 'Place on a match to sacrifice it (that match always scores 0, scoreline ignored; drops any vote / All-or-nothing / Safety net on that match). Then drag Multiplier (×1.7) or Bounty (stake 5–20 Stack) onto a different match. Costs 2 tactical points for the pair.',
    color: 'red',
    svg: getAugmentDefaultSvgPath('d', AUGMENT_KIND.MATCH)
  },
  {
    value: 'm',
    label: 'MUL',
    fullLabel: 'Multiplier',
    cardLabel: 'Multiplier',
    shortDesc: '×1.7 on this match — place Sacrifice first',
    desc: 'Linked to Sacrifice (0 TP). Drag onto a different match after placing Sacrifice. Multiplies that match\'s rarity multiplier by 1.7 (70% increase). Any +0.1 community vote boost is added after, not multiplied. Cannot be combined with Bounty.',
    color: 'gold',
    svg: getAugmentDefaultSvgPath('m', AUGMENT_KIND.MATCH)
  },
  {
    value: 'b',
    label: 'BTY',
    fullLabel: 'Bounty',
    cardLabel: 'Bounty',
    shortDesc: '+N if 1×2 correct — stake 5–20 Stack (Sacrifice first)',
    desc: 'Linked to Sacrifice (0 TP). Drag onto a different match after placing Sacrifice, then set a 5–20 stake from your season Stack. Stack is consumed on Save. If you hit the 1×2, that match gets +N flat (not multiplied). Miss and the bonus is 0; Stack stays spent. Cannot be combined with Multiplier.',
    color: 'gold',
    svg: getAugmentDefaultSvgPath('b', AUGMENT_KIND.MATCH)
  }
];

export const DEFAULT_AUGMENTS = attachTacticalPointsCost(DEFAULT_AUGMENT_DEFS);

export {
  STYLE_ID,
  GOLD_STYLE_ID,
  SILVER_STYLE_ID,
  CYBERPUNK_STYLE_ID,
  TROPICAL_STYLE_ID,
  BLOOM_STYLE_ID,
  JELLY_STYLE_ID,
  CRYSTAL_STYLE_ID,
  BRAZIL_STYLE_ID,
  WINTER_STYLE_ID,
  PENALTY_FAMILY_STYLE_ID,
  getStoreStyleName,
  DEFAULT_STORE_STYLE_NAME
} from 'arena-shared';

/** Purchasable style ids for a store play group (from shared catalog). */
function getStoreStyleIdsForGroupId(groupId) {
  const group = STORE_CATALOG.find((g) => g.id === groupId);
  return group ? group.styles.map((s) => s.id) : [STYLE_ID];
}

function buildStoreCatalogItem({
  id,
  augmentCodes,
  label,
  color,
  styleSvg,
  styleSvgs,
  styleName,
  shortDesc = '',
  styleId = STYLE_ID,
  kind = AUGMENT_KIND.MATCH
}) {
  const svgs =
    Array.isArray(styleSvgs) && styleSvgs.length > 0
      ? styleSvgs
      : styleSvg
        ? [styleSvg]
        : [];
  return {
    id,
    augmentCodes,
    styleId,
    cost: getStoreStyleCost(id, styleId) ?? getStoreStyleCost(augmentCodes[0], styleId),
    label,
    styleName: styleName ?? getStoreStyleName(styleId),
    shortDesc,
    color,
    kind,
    styleSvg: svgs[0],
    styleSvgs: svgs
  };
}

/** Style asset for a store row (grouped home/away plays use one card). */
export function getStoreGroupStyleSvg(storeKey, styleId = STYLE_ID) {
  if (WINNER_ICON_CODES.includes(storeKey)) {
    return clashWinnerIconSrc(formatClashWinnerIcon(storeKey, styleId));
  }
  return getAugmentStyleSvgPath(storeKey, styleId, AUGMENT_KIND.MATCH);
}

// All plays combined (for lookup)
export const ALL_AUGMENTS = [...DEFAULT_AUGMENTS];

// Legacy: AUGMENTS = default only (for backward compat in About, etc.)
export const AUGMENTS = DEFAULT_AUGMENTS;

/** @param {unknown} inventory whole inventory object from JWT */
export function getInventoryStyles(inventory) {
  if (inventory == null || typeof inventory !== 'object' || Array.isArray(inventory)) return {};
  const s = inventory.s;
  if (s == null || typeof s !== 'object' || Array.isArray(s)) return {};
  return s;
}

export function ownsAugmentStyle(inventory, augmentCode, styleId) {
  const base = getAugmentBaseCode(String(augmentCode));
  if (isOneGoalAugmentCode(base)) {
    return ONE_GOAL_AUGMENT_CODES.some((code) => inventoryHasStyle(inventory, code, styleId));
  }
  return inventoryHasStyle(inventory, base, styleId);
}

function inventoryHasStyle(inventory, augmentCode, styleId) {
  const ids = getInventoryStyles(inventory)[augmentCode];
  return Array.isArray(ids) && ids.includes(styleId);
}

export function getOwnedStyleIds(inventory, augmentCode) {
  const base = getAugmentBaseCode(String(augmentCode));
  if (isOneGoalAugmentCode(base)) {
    const merged = new Set();
    for (const code of ONE_GOAL_AUGMENT_CODES) {
      for (const id of inventoryHasStyleIds(inventory, code)) {
        merged.add(id);
      }
    }
    return sortStyleIdsByReleaseDesc([...merged]);
  }
  return inventoryHasStyleIds(inventory, base);
}

function inventoryHasStyleIds(inventory, augmentCode) {
  const ids = getInventoryStyles(inventory)[augmentCode];
  if (!Array.isArray(ids)) return [];
  return ids;
}

/** @typedef {{ styleId: number | null, name: string, svg: string }} AugmentStyleChoice */

/** Default + owned style previews for the equip popover. */
export function getAugmentStyleChoices(baseCode, inventory) {
  if (!baseCode) return [];
  const meta = getAugmentMeta(baseCode);
  /** @type {AugmentStyleChoice[]} */
  const choices = [
    {
      styleId: null,
      name: 'Default',
      svg: meta?.svg ?? getAugmentDefaultSvgPath('2x', AUGMENT_KIND.MATCH)
    }
  ];
  for (const styleId of getOwnedStyleIds(inventory, baseCode)) {
    const entry = AUGMENT_STYLES[baseCode]?.[styleId];
    choices.push({
      styleId,
      name: entry?.name ?? DEFAULT_STORE_STYLE_NAME,
      svg: entry?.svg ?? getAugmentStyleSvgPath(baseCode, styleId)
    });
  }
  return choices;
}

/** Owned style ids for a pool carousel card (merges home/away variant inventory). */
export function getOwnedStyleIdsForPoolAugment(poolValue, inventory) {
  const group = getTeamSideAugmentGroup(poolValue);
  if (!group) return getOwnedStyleIds(inventory, poolValue);
  const ids = new Set();
  for (const code of group.variants) {
    for (const id of getOwnedStyleIds(inventory, code)) {
      ids.add(id);
    }
  }
  return sortStyleIdsByReleaseDesc([...ids]);
}

/** Style picker options for a pool carousel card. */
export function getAugmentStyleChoicesForPool(poolValue, inventory) {
  const group = getTeamSideAugmentGroup(poolValue);
  const styleCode = group ? group.home : poolValue;
  return getAugmentStyleChoices(styleCode, inventory);
}

/** Preview image for a pool card with the selected style. */
export function getPoolAugmentDisplaySvg(poolValue, styleId, inventory) {
  const group = getTeamSideAugmentGroup(poolValue);
  const meta = group?.uiAugment ?? getAugmentMeta(poolValue);
  if (styleId == null) return meta?.svg ?? null;
  const styleCode = group ? group.home : poolValue;
  return (
    getAugmentDisplaySvg(formatAugmentRef(styleCode, styleId), inventory) ??
    AUGMENT_STYLES[styleCode]?.[styleId]?.svg ??
    meta?.svg ??
    null
  );
}

/**
 * SVG for display: encoded ref (2x#1) or base code with optional inventory for validation.
 * @param {string} augmentRef
 * @param {unknown} [inventory]
 */
export function getAugmentDisplaySvg(augmentRef, inventory) {
  const { code, styleId } = parseAugmentRef(augmentRef);
  const meta = getAugmentByValue(code) ?? getClashAugmentOption(code);
  if (styleId == null) return meta?.svg ?? null;
  if (inventory && !ownsAugmentStyle(inventory, code, styleId)) return meta?.svg ?? null;
  return AUGMENT_STYLES[code]?.[styleId]?.svg ?? meta?.svg ?? null;
}

/** One Goal and legacy side codes (scoring treats all as `g`). */
export const ONE_GOAL_AUGMENT_CODES = ['g', '+1h', '+1a'];

export function isOneGoalAugmentCode(value) {
  if (!value || typeof value !== 'string') return false;
  const { code } = parseAugmentRef(value);
  return ONE_GOAL_AUGMENT_CODES.includes(code);
}

/** @deprecated Use {@link isOneGoalAugmentCode} */
export function isPlusOneGoalVariant(value) {
  return isOneGoalAugmentCode(value);
}

/** @deprecated UI pool id retained for store asset paths only. */
export const PLUS_ONE_GOAL_UI_VALUE = '+1goal';
/** @deprecated Legacy backend codes; new placements use `g`. */
export const PLUS_ONE_GOAL_VARIANTS = ['+1h', '+1a'];

export const PENALTY_UI_VALUE = 'penalty';
export const PENALTY_VARIANTS = ['hp', 'ap'];

export const MISSED_PENALTY_UI_VALUE = 'missed-pk';
export const MISSED_PENALTY_VARIANTS = ['mph', 'mpa'];

export const RED_CARD_UI_VALUE = 'red-card';
export const RED_CARD_VARIANTS = ['rch', 'rca'];

/** @typedef {{ uiValue: string, variants: string[], home: string, away: string, uiAugment: object }} TeamSideAugmentGroup */

/** @type {TeamSideAugmentGroup[]} */
export const TEAM_SIDE_AUGMENT_GROUPS = [
  {
    uiValue: PENALTY_UI_VALUE,
    variants: PENALTY_VARIANTS,
    home: 'hp',
    away: 'ap',
    uiAugment: createTeamSideUiAugment({
      value: PENALTY_UI_VALUE,
      displayName: 'Scored PK',
      shortDesc: '+4 if that team scores a successful penalty kick',
      desc: 'Drop on home or away. +4 bonus on top of normal match scoring if that team scores from the penalty spot. Up to twice per round, either side.',
      color: 'cyan',
      svg: getAugmentDefaultSvgPath('penalty', AUGMENT_KIND.MATCH),
      tacticalPointsCost: AUGMENT_TACTICAL_POINTS_COST.hp
    })
  },
  {
    uiValue: MISSED_PENALTY_UI_VALUE,
    variants: MISSED_PENALTY_VARIANTS,
    home: 'mph',
    away: 'mpa',
    uiAugment: createTeamSideUiAugment({
      value: MISSED_PENALTY_UI_VALUE,
      displayName: 'Missed PK',
      shortDesc: 'Drop on home or away - +8 if that team misses a penalty',
      desc: 'Drop on home or away. +8 bonus on top of normal match scoring if that team misses a penalty (saved, off the post, over the bar, etc.). Up to twice per round, either side.',
      color: 'orange',
      svg: getAugmentDefaultSvgPath('missed-pk', AUGMENT_KIND.MATCH),
      tacticalPointsCost: AUGMENT_TACTICAL_POINTS_COST.mph
    })
  },
  {
    uiValue: RED_CARD_UI_VALUE,
    variants: RED_CARD_VARIANTS,
    home: 'rch',
    away: 'rca',
    uiAugment: createTeamSideUiAugment({
      value: RED_CARD_UI_VALUE,
      displayName: 'Red Bonus',
      shortDesc: 'Drop on home or away - +5 if that team gets a red card',
      desc: 'Drop on home or away. +5 bonus on top of normal match scoring if that team receives a red card (any player sent off on that side). Up to twice per round, either side.',
      color: 'red',
      svg: getAugmentDefaultSvgPath('red-card', AUGMENT_KIND.MATCH),
      tacticalPointsCost: AUGMENT_TACTICAL_POINTS_COST.rch
    })
  }
];

export const PLUS_ONE_GOAL_UI_AUGMENT =
  DEFAULT_AUGMENTS.find((a) => a.value === 'g') ?? null;
export const PENALTY_UI_AUGMENT = TEAM_SIDE_AUGMENT_GROUPS[0].uiAugment;
export const MISSED_PENALTY_UI_AUGMENT = TEAM_SIDE_AUGMENT_GROUPS[1].uiAugment;
export const RED_CARD_UI_AUGMENT = TEAM_SIDE_AUGMENT_GROUPS[2].uiAugment;

const TEAM_SIDE_VARIANT_SET = new Set(
  TEAM_SIDE_AUGMENT_GROUPS.flatMap((g) => g.variants)
);

const CLASH_AUGMENT_CARD_COLOR = { sa: 'gold', bs: 'cyan', eif: 'green', gd: 'gold' };

const STANDALONE_STORE_AUGMENTS = DEFAULT_AUGMENTS.filter(
  (a) => !TEAM_SIDE_VARIANT_SET.has(a.value)
);

/** @typedef {{ id: string, augmentCodes: string[], label: string, shortDesc: string, color: string, kind: string, styleIds: number[] }} StoreAugmentGroup */

function buildStoreAugmentGroup({ id, augmentCodes, label, shortDesc, color, kind, styleIds }) {
  return {
    id,
    augmentCodes,
    label,
    shortDesc,
    color,
    kind: kind ?? (augmentCodes.length === 1 ? getAugmentKind(augmentCodes[0]) : AUGMENT_KIND.MATCH),
    styleIds: styleIds ?? getStoreStyleIdsForGroupId(id)
  };
}

/** Unique style art paths for a store group (dedupes shared team-side assets). */
function getStoreGroupStyleSvgsForGroup(group, styleId) {
  if (WINNER_ICON_CODES.includes(group.id)) {
    return [clashWinnerIconSrc(formatClashWinnerIcon(group.id, styleId))];
  }
  const paths = [];
  const seen = new Set();
  for (const code of group.augmentCodes) {
    const path = getAugmentStyleSvgPath(code, styleId, group.kind);
    if (seen.has(path)) continue;
    seen.add(path);
    paths.push(path);
  }
  if (paths.length === 0) {
    const slug = group.augmentCodes[0] ?? group.id;
    paths.push(getAugmentStyleSvgPath(slug, styleId, group.kind));
  }
  return paths;
}

const WINNER_STORE_META = {
  c: {
    label: 'Crown',
    shortDesc: 'Clash winner badge — gold crown style',
    color: 'gold'
  },
  l: {
    label: 'L',
    shortDesc: 'Clash winner badge — gold L style',
    color: 'gold'
  },
  f: {
    label: 'Flex',
    shortDesc: 'Clash winner badge — gold flex style',
    color: 'gold'
  }
};

/** Store catalog grouped by play type; each group lists its purchasable styles. */
export const STORE_AUGMENT_GROUPS = STORE_CATALOG.map((catalogGroup) => {
  const standalone = STANDALONE_STORE_AUGMENTS.find((a) => a.value === catalogGroup.id);
  if (standalone) {
    return buildStoreAugmentGroup({
      id: catalogGroup.id,
      augmentCodes: catalogGroup.codes,
      label: standalone.displayName,
      shortDesc: standalone.shortDesc,
      color: standalone.color,
      styleIds: catalogGroup.styles.map((s) => s.id)
    });
  }
  const teamSide = TEAM_SIDE_AUGMENT_GROUPS.find((g) => g.uiValue === catalogGroup.id);
  if (teamSide) {
    return buildStoreAugmentGroup({
      id: catalogGroup.id,
      augmentCodes: catalogGroup.codes,
      label: teamSide.uiAugment.displayName,
      shortDesc: teamSide.uiAugment.shortDesc,
      color: teamSide.uiAugment.color,
      styleIds: catalogGroup.styles.map((s) => s.id)
    });
  }
  const clash = CLASH_AUGMENT_OPTIONS.find((a) => a.value === catalogGroup.id);
  if (clash) {
    return buildStoreAugmentGroup({
      id: catalogGroup.id,
      augmentCodes: catalogGroup.codes,
      label: clash.displayName,
      shortDesc: clash.shortDesc,
      color: CLASH_AUGMENT_CARD_COLOR[catalogGroup.id] ?? 'gold',
      kind: AUGMENT_KIND.CLASH,
      styleIds: catalogGroup.styles.map((s) => s.id)
    });
  }
  const round = DEFAULT_ROUND_AUGMENTS.find((a) => a.value === catalogGroup.id);
  if (round) {
    return buildStoreAugmentGroup({
      id: catalogGroup.id,
      augmentCodes: catalogGroup.codes,
      label: round.fullLabel ?? round.label,
      shortDesc: round.shortDesc,
      color: round.color,
      kind: AUGMENT_KIND.ROUND,
      styleIds: catalogGroup.styles.map((s) => s.id)
    });
  }
  const winner = WINNER_STORE_META[catalogGroup.id];
  if (winner) {
    return buildStoreAugmentGroup({
      id: catalogGroup.id,
      augmentCodes: catalogGroup.codes,
      label: winner.label,
      shortDesc: winner.shortDesc,
      color: winner.color,
      kind: 'winner',
      styleIds: catalogGroup.styles.map((s) => s.id)
    });
  }
  return buildStoreAugmentGroup({
    id: catalogGroup.id,
    augmentCodes: catalogGroup.codes,
    label: catalogGroup.id,
    shortDesc: '',
    color: 'gold',
    styleIds: catalogGroup.styles.map((s) => s.id)
  });
});

/** @type {Record<string, Record<number, { svg: string, name: string, cost: number }>>} */
export const AUGMENT_STYLES = {};
for (const aug of [...DEFAULT_AUGMENTS, ...DEFAULT_ROUND_AUGMENTS, ...CLASH_AUGMENT_OPTIONS]) {
  AUGMENT_STYLES[aug.value] = {
    [STYLE_ID]: {
      svg: getAugmentStyleSvgPath(aug.value, STYLE_ID),
      name: getStoreStyleName(STYLE_ID),
      cost: getStoreStyleCost(aug.value, STYLE_ID)
    }
  };
}
for (const group of STORE_AUGMENT_GROUPS) {
  for (const styleId of group.styleIds) {
    for (const code of group.augmentCodes) {
      if (!AUGMENT_STYLES[code]) AUGMENT_STYLES[code] = {};
      if (styleId === STYLE_ID && AUGMENT_STYLES[code][STYLE_ID]) continue;
      const svg = WINNER_ICON_CODES.includes(group.id)
        ? clashWinnerIconSrc(formatClashWinnerIcon(code, styleId))
        : getAugmentStyleSvgPath(code, styleId, group.kind);
      AUGMENT_STYLES[code][styleId] = {
        svg,
        name: getStoreStyleName(styleId),
        cost: getStoreStyleCost(group.id, styleId)
      };
    }
  }
}

/** Flat store rows (one per play type × style); used for purchases and inventory. */
export const STORE_STYLES = STORE_AUGMENT_GROUPS.flatMap((group) =>
  group.styleIds.map((styleId) =>
    buildStoreCatalogItem({
      id: group.id,
      augmentCodes: group.augmentCodes,
      label: group.label,
      shortDesc: group.shortDesc,
      color: group.color,
      kind: group.kind,
      styleId,
      styleSvgs: getStoreGroupStyleSvgsForGroup(group, styleId)
    })
  )
);

export function ownsStoreStyleItem(inventory, item) {
  return item.augmentCodes.every((code) => ownsAugmentStyle(inventory, code, item.styleId));
}

/** Body for POST /store/buy from a store catalog row. */
export function getStoreBuyPayload(item) {
  if (item.augmentCodes.length === 1) {
    return { augment: item.augmentCodes[0], styleId: item.styleId };
  }
  return { storeGroup: item.id, styleId: item.styleId };
}

/**
 * Map current rotation offerings to store catalog rows (same shape as STORE_STYLES).
 * @param {number} [now]
 * @returns {{ items: ReturnType<typeof buildStoreCatalogItem>[], windowStart: number, windowEnd: number, windowIndex: number }}
 */
export function getActiveStoreCatalog(now = Date.now()) {
  const offerings = getStoreOfferings(now);
  const byKey = new Map(STORE_STYLES.map((item) => [`${item.id}#${item.styleId}`, item]));
  const items = offerings.items
    .map((o) => byKey.get(`${o.groupId}#${o.styleId}`))
    .filter(Boolean);
  return {
    items,
    windowStart: offerings.windowStart,
    windowEnd: offerings.windowEnd,
    windowIndex: offerings.windowIndex
  };
}

/**
 * Active store groups for the current window (only groups with ≥1 offered style).
 * @param {number} [now]
 */
export function getActiveStoreGroups(now = Date.now()) {
  const { items, windowStart, windowEnd, windowIndex } = getActiveStoreCatalog(now);
  const styleIdsByGroup = new Map();
  for (const item of items) {
    if (!styleIdsByGroup.has(item.id)) styleIdsByGroup.set(item.id, []);
    styleIdsByGroup.get(item.id).push(item.styleId);
  }
  const groups = STORE_AUGMENT_GROUPS.filter((g) => styleIdsByGroup.has(g.id)).map((g) => ({
    ...g,
    styleIds: styleIdsByGroup.get(g.id)
  }));
  return { groups, items, windowStart, windowEnd, windowIndex };
}

function getInventoryStyleRowsForGroup(inventory, group) {
  const rows = [];
  const styleIds = new Set();
  for (const code of group.augmentCodes) {
    for (const id of getOwnedStyleIds(inventory, code)) {
      styleIds.add(id);
    }
  }
  for (const styleId of sortStyleIdsByReleaseDesc([...styleIds])) {
    if (!group.augmentCodes.every((code) => ownsAugmentStyle(inventory, code, styleId))) continue;
    const item =
      STORE_STYLES.find((row) => row.id === group.id && row.styleId === styleId) ??
      buildStoreCatalogItem({
        id: group.id,
        augmentCodes: group.augmentCodes,
        label: group.label,
        shortDesc: group.shortDesc,
        color: group.color,
        kind: group.kind,
        styleId,
        styleSvgs: getStoreGroupStyleSvgsForGroup(group, styleId)
      });
    const styleName =
      AUGMENT_STYLES[group.augmentCodes[0]]?.[styleId]?.name ?? getStoreStyleName(styleId);
    rows.push({ item, styleId, styleName });
  }
  return rows;
}

/** Play-styles inventory grouped by play bucket (excludes clash winner badges). */
export function getInventoryStyleDisplayGroups(inventory) {
  return STORE_AUGMENT_GROUPS.filter((group) => group.kind !== 'winner')
    .map((group) => ({
      group,
      rows: getInventoryStyleRowsForGroup(inventory, group)
    }))
    .filter(({ rows }) => rows.length > 0);
}

/** Flat rows for account/profile inventory (matches store cards). */
export function getInventoryStyleDisplayRows(inventory) {
  return getInventoryStyleDisplayGroups(inventory).flatMap(({ rows }) => rows);
}

export function getTeamSideAugmentGroup(value) {
  if (!value) return null;
  const code = getAugmentBaseCode(value);
  return (
    TEAM_SIDE_AUGMENT_GROUPS.find((g) => g.uiValue === code || g.variants.includes(code)) ?? null
  );
}

/** Align per-side backend codes with pooled UI names (single source: `uiAugment.displayName`). */
function syncTeamSideAugmentCatalog() {
  for (const group of TEAM_SIDE_AUGMENT_GROUPS) {
    for (const code of group.variants) {
      const aug = DEFAULT_AUGMENTS.find((a) => a.value === code);
      if (!aug) continue;
      const synced = normalizeAugmentDef({
        ...aug,
        displayName: group.uiAugment.displayName,
        shortDesc: group.uiAugment.shortDesc,
        svg: group.uiAugment.svg,
        color: group.uiAugment.color
      });
      Object.assign(aug, synced);
    }
  }
}
syncTeamSideAugmentCatalog();

/**
 * Canonical play metadata for any code (backend, UI pool id, or `code#style` ref).
 * @param {string} codeOrRef
 */
export function getAugmentMeta(codeOrRef) {
  if (!codeOrRef) return null;
  const { code } = parseAugmentRef(String(codeOrRef));
  if (!code) return null;

  if (code === '+1h' || code === '+1a') {
    return ALL_AUGMENTS.find((a) => a.value === 'g') ?? null;
  }

  const group = getTeamSideAugmentGroup(code);
  if (group) {
    if (code === group.uiValue) return group.uiAugment;
    const backend = ALL_AUGMENTS.find((a) => a.value === code);
    if (backend) return backend;
    return group.uiAugment;
  }

  const match = ALL_AUGMENTS.find((a) => a.value === code);
  if (match) return match;

  const clash = CLASH_AUGMENT_OPTIONS.find((o) => o.value === code);
  if (clash) return clash;

  const round = DEFAULT_ROUND_AUGMENTS.find((a) => a.value === code);
  if (round) return normalizeAugmentDef(round);

  const legacy = LEGACY_MATCH_AUGMENT_META[code];
  if (legacy) return legacy;

  if (code === 'et') {
    return KNOCKOUT_UI_AUGMENTS.find((a) => a.value === 'et') ?? null;
  }
  if (code === 'ph' || code === 'pa') {
    return KNOCKOUT_UI_AUGMENTS.find((a) => a.value === SHOOTOUT_UI_VALUE) ?? null;
  }

  return null;
}

/** User-facing play name (single source of truth for labels). */
export function getAugmentDisplayName(codeOrRef) {
  return getAugmentMeta(codeOrRef)?.displayName ?? String(codeOrRef ?? '');
}

export function getAugmentShortDesc(codeOrRef) {
  return getAugmentMeta(codeOrRef)?.shortDesc ?? '';
}

/** @deprecated Use {@link getAugmentDisplayName} */
export function getAugmentLabel(value) {
  return getAugmentDisplayName(value);
}

export function getAugmentByValue(value) {
  return getAugmentMeta(value);
}

/** Intro copy for match play pool help (Betting). */
export function getMatchAugmentPoolHelpLead(tacticalCap = TACTICAL_POINTS_PER_ROUND) {
  const pooled = TEAM_SIDE_AUGMENT_GROUPS.map((g) => g.uiAugment.displayName).join(', ');
  return `Drag onto a match to attach. ${pooled} drop on the home or away side where noted. Each costs tactical points from the same ${tacticalCap} budget as round plays. Remove by dragging back to the pool.`;
}

/** Intro copy for round play pool help (Betting). */
export function getRoundAugmentPoolHelpLead(tacticalCap = TACTICAL_POINTS_PER_ROUND) {
  return `Pick at most one for the whole round. It shares your ${tacticalCap} tactical-point budget with match plays until the round locks.`;
}

/** Intro copy for clash play pool help (Clash sheet). */
export function getClashAugmentPoolHelpLead(maxPerClash = CLASH_TACTICAL_POINTS_MAX_PER_CLASH) {
  return `Drag onto a match against this opponent. Each type once per opponent. Up to ${maxPerClash} tactical points per opponent (separate from your main card budget). Remove by dragging back to the pool.`;
}

export function isPenaltyVariant(value) {
  return value === 'hp' || value === 'ap';
}

export function isMissedPenaltyVariant(value) {
  return value === 'mph' || value === 'mpa';
}

export function isRedCardVariant(value) {
  return value === 'rch' || value === 'rca';
}

export function isTeamSideAugmentVariant(value) {
  return TEAM_SIDE_VARIANT_SET.has(getAugmentBaseCode(value));
}

export function isHomeTeamSideAugment(value) {
  const g = getTeamSideAugmentGroup(value);
  return g != null && getAugmentBaseCode(value) === g.home;
}

export function isAwayTeamSideAugment(value) {
  const g = getTeamSideAugmentGroup(value);
  return g != null && getAugmentBaseCode(value) === g.away;
}

export function isPlusOneGoalUiDrag(value) {
  return getAugmentBaseCode(String(value)) === 'g';
}

export function isPenaltyUiDrag(value) {
  const g = getTeamSideAugmentGroup(value);
  return g?.uiValue === PENALTY_UI_VALUE;
}

export function isMissedPenaltyUiDrag(value) {
  const g = getTeamSideAugmentGroup(value);
  return g?.uiValue === MISSED_PENALTY_UI_VALUE;
}

export function isRedCardUiDrag(value) {
  const g = getTeamSideAugmentGroup(value);
  return g?.uiValue === RED_CARD_UI_VALUE;
}

export function isTeamSideAugmentUiDrag(value) {
  return getTeamSideAugmentGroup(value) != null;
}

/** @deprecated Side-specific One Goal removed; placements use `g`. */
export function resolvePlusOneGoalVariant(_side) {
  return 'g';
}

/** @deprecated Side-specific One Goal removed. */
export function getPlusOneGoalImpossibleSideWarning(
  _predictedHome,
  _predictedAway,
  _augmentCodeOrRef
) {
  return null;
}

/** @param {'home' | 'away'} side */
export function resolvePenaltyVariant(side) {
  return side === 'away' ? 'ap' : 'hp';
}

/** @param {'home' | 'away'} side */
export function resolveMissedPenaltyVariant(side) {
  return side === 'away' ? 'mpa' : 'mph';
}

/** @param {'home' | 'away'} side */
export function resolveRedCardVariant(side) {
  return side === 'away' ? 'rca' : 'rch';
}

/** @param {string} uiValue @param {{ [backendCode: string]: unknown }} augmentUsedByGame */
/** Max placements per team-side group per round (either side). */
export const TEAM_SIDE_AUGMENT_MAX_USES = 2;

/**
 * @param {string} uiValue
 * @param {Iterable<string>} placedAugmentBases — base codes currently on matches
 */
export function getTeamSideUiPoolState(uiValue, placedAugmentBases) {
  const g = TEAM_SIDE_AUGMENT_GROUPS.find((x) => x.uiValue === uiValue);
  if (!g) return { isUsed: false, isPartial: false, useCount: 0, homeUsedOn: null, awayUsedOn: null };
  const bases = [...(placedAugmentBases || [])].map((c) => getAugmentBaseCode(c));
  const useCount = bases.filter((b) => g.variants.includes(b)).length;
  const homeUsedOn = bases.includes(g.home) ? g.home : null;
  const awayUsedOn = bases.includes(g.away) ? g.away : null;
  const isUsed = useCount >= TEAM_SIDE_AUGMENT_MAX_USES;
  const isPartial = useCount > 0 && !isUsed;
  return { isUsed, isPartial, useCount, homeUsedOn, awayUsedOn, group: g };
}

/** Pool carousel / touch ghost meta for UI-only play codes. */
export function getBettingUiAugmentMeta(value) {
  return getAugmentMeta(value);
}

/** Match plays for the betting pool carousel (one card per home/away pair). */
export function getPlayerAugmentsForBettingUi({ includeKnockout = false } = {}) {
  let augments = [...DEFAULT_AUGMENTS];
  for (const group of TEAM_SIDE_AUGMENT_GROUPS) {
    if (!augments.some((a) => group.variants.includes(a.value))) continue;
    augments = [...augments.filter((a) => !group.variants.includes(a.value)), group.uiAugment];
  }
  if (includeKnockout) {
    augments = [...augments, ...KNOCKOUT_UI_AUGMENTS];
  }
  return augments;
}

export const KNOCKOUT_UI_AUGMENTS = [
  normalizeAugmentDef({
    value: 'et',
    displayName: 'Extra time',
    shortDesc: '120-min scoreline - +5 if ET played; up to 20 for exact at 120',
    desc: 'Knockout stages only. Replaces normal 90-minute scoring-your prediction is judged against the score after extra time. No points if the match ends in 90 minutes. +5 if extra time is played; +10 for the correct 1×2 result at 120 minutes; +20 for the exact score at 120 minutes.',
    color: 'cyan',
    svg: getAugmentDefaultSvgPath('et', AUGMENT_KIND.MATCH),
    tacticalPointsCost: AUGMENT_TACTICAL_POINTS_COST.et
  }),
  createTeamSideUiAugment({
    value: SHOOTOUT_UI_VALUE,
    displayName: 'Shootout',
    shortDesc: '+5 for penalty winner, on top of your 120-min draw points',
    desc:
      'Knockout stages only. Predict a draw, drop on home or away for your penalty winner. 0 pts unless level after extra time.\n\n' +
      'Score the 120-min result as usual (20 pts exact, 10 pts correct draw), plus +5 if you pick the right penalty winner.',
    color: 'gold',
    svg: getAugmentDefaultSvgPath('shootout', AUGMENT_KIND.MATCH),
    tacticalPointsCost: AUGMENT_TACTICAL_POINTS_COST.ph
  })
];

export function isKnockoutMatchGame(game) {
  return game?.stage != null && game.stage !== '' && game.stage !== 'GROUP_STAGE';
}

export function roundHasKnockoutGames(round) {
  if (!round?.games) return false;
  return round.games.some((g) => isKnockoutMatchGame(g) && isGameOnRoundCard(g));
}

export function resolveShootoutVariant(side) {
  return side === 'away' ? 'pa' : 'ph';
}

export function isShootoutUiDrag(value) {
  return getAugmentBaseCode(String(value)) === SHOOTOUT_UI_VALUE;
}

export function isKnockoutOnlyAugmentCode(code) {
  return KNOCKOUT_MATCH_AUGMENT_CODES.includes(getAugmentBaseCode(String(code)));
}

/** About page copy: no em or en dashes (use hyphens for scores, periods for breaks). */
function sanitizeAboutCopy(text) {
  if (!text) return text;
  return String(text)
    .replace(/\s*\u2014\s*/g, '. ')
    .replace(/\u2014/g, '. ')
    .replace(/\u2013/g, '-');
}

function withAboutCopy(augment) {
  return {
    ...augment,
    desc: sanitizeAboutCopy(augment.desc),
    shortDesc: sanitizeAboutCopy(augment.shortDesc)
  };
}

/** Help popover: one entry per combined home/away play pair. */
export function getMatchAugmentsForHelp() {
  const rest = ALL_AUGMENTS.filter((a) => !TEAM_SIDE_VARIANT_SET.has(a.value));
  const combined = TEAM_SIDE_AUGMENT_GROUPS.map((g) => ({
    value: g.uiValue,
    displayName: g.uiAugment.displayName,
    cardLabel: g.uiAugment.displayName,
    shortDesc: g.uiAugment.shortDesc,
    desc: g.uiAugment.aboutDesc ?? g.uiAugment.desc,
    tacticalPointsCost: g.uiAugment.tacticalPointsCost,
    color: g.uiAugment.color,
    svg: g.uiAugment.svg
  }));
  return [...rest, ...combined].map(withAboutCopy);
}

/** About / help: round-level plays with display names normalized. */
export function getRoundAugmentsForHelp() {
  return DEFAULT_ROUND_AUGMENTS.map((a) =>
    withAboutCopy(
      normalizeAugmentDef({
        ...a,
        displayName: a.fullLabel ?? a.label ?? a.value
      })
    )
  );
}

/** About / help: clash plays for weekly head-to-head matchups. */
export function getClashAugmentsForHelp() {
  return CLASH_AUGMENT_OPTIONS.map((a) =>
    withAboutCopy({
      ...a,
      desc: a.desc ?? a.shortDesc,
      color: a.color ?? CLASH_AUGMENT_CARD_COLOR[a.value] ?? 'gold'
    })
  );
}

/** About / help: knockout-only match augments. */
export function getKnockoutAugmentsForHelp() {
  return KNOCKOUT_UI_AUGMENTS.map((a) =>
    withAboutCopy({
      value: a.value,
      displayName: a.displayName,
      cardLabel: a.displayName,
      shortDesc: a.shortDesc,
      desc: a.desc,
      tacticalPointsCost: a.tacticalPointsCost,
      color: a.color,
      svg: a.svg
    })
  );
}

/**
 * Resolve drag payload to a backend play code (or null if side required but missing).
 * @param {string} dragValue
 * @param {'home' | 'away' | null | undefined} dropSide
 */
export function resolveAugmentValueForDrop(dragValue, dropSide) {
  if (!dragValue) return null;
  const baseCode = getAugmentBaseCode(dragValue);
  if (baseCode === SHOOTOUT_UI_VALUE) {
    if (!dropSide) return null;
    return resolveShootoutVariant(dropSide);
  }
  const group = getTeamSideAugmentGroup(dragValue);
  if (!group) return dragValue;
  if (baseCode === group.uiValue) {
    if (!dropSide) return null;
    return dropSide === 'away' ? group.away : group.home;
  }
  if (!dropSide) return dragValue;
  return dropSide === 'away' ? group.away : group.home;
}

/** @param {number} clientX */
export function resolveDropSideFromPoint(clientX, gameTeamsEl) {
  if (gameTeamsEl == null || typeof clientX !== 'number') return null;
  const rect = gameTeamsEl.getBoundingClientRect();
  const mid = rect.left + rect.width / 2;
  return clientX < mid ? 'home' : 'away';
}

/**
 * @param {Event | { clientX?: number, changedTouches?: TouchList, target?: EventTarget | null }} e
 * @param {HTMLElement | null} gameCardEl
 */
export function getDropSideFromPointerEvent(e, gameCardEl) {
  if (!gameCardEl) return null;
  const target = e.target;
  if (target && typeof target.closest === 'function') {
    const sideEl = target.closest('[data-augment-drop-side]');
    if (sideEl && gameCardEl.contains(sideEl)) {
      return sideEl.dataset.augmentDropSide === 'away' ? 'away' : 'home';
    }
  }
  const x = e.clientX ?? e.changedTouches?.[0]?.clientX;
  const teams = gameCardEl.querySelector('.game-teams');
  return resolveDropSideFromPoint(x, teams);
}

const OFF_ROUND_CARD_STATUSES = new Set(['finished', 'postponed', 'canceled', 'cancelled']);
const VOID_GAME_STATUSES = new Set(['postponed', 'canceled', 'cancelled']);

/**
 * Games still on the round card (not finished/postponed/canceled). Used for tactical points and plays while the round is open.
 */
export function isGameOnRoundCard(game) {
  if (!game) return false;
  return !OFF_ROUND_CARD_STATUSES.has(String(game.status || '').toLowerCase());
}

/**
 * Postponed/canceled (or explicit voidResult) — not scored; hide pick/recap UI.
 */
export function isVoidedGame(game) {
  if (!game) return false;
  if (game.voidResult === true) return true;
  return VOID_GAME_STATUSES.has(String(game.status || '').toLowerCase());
}

/** Postponed fixtures stay off the card but players may reclaim match plays from them. */
export function canRemoveAugmentFromGame(game) {
  if (!game) return false;
  return String(game.status || '').toLowerCase() === 'postponed';
}

/** @deprecated use isGameOnRoundCard; kept for any older imports */
export function isGameOpenForBetting(game, now = Date.now()) {
  if (!isGameOnRoundCard(game)) return false;
  const t = game.gameDate;
  if (t == null) return false;
  const fiveMinutes = 5 * 60 * 1000;
  return t - now > fiveMinutes;
}

function normalizeBets(bets) {
  const out = {};
  if (!bets) return out;
  for (const [k, v] of Object.entries(bets)) {
    out[String(k)] = v;
  }
  return out;
}

/** Sum clash tactical points across open games (each bet may carry `clashAugments` map). */
export function totalClashTacticalPointsForOpenGames(round, now = Date.now()) {
  if (!round?.games) return 0;
  const bets = normalizeBets(round.bets);
  let total = 0;
  for (const game of round.games) {
    if (!isGameOnRoundCard(game)) continue;
    const ca = bets[String(game.gameId)]?.clashAugments;
    if (!ca || typeof ca !== 'object') continue;
    for (const v of Object.values(ca)) {
      total += getClashAugmentTacticalPointsCost(v);
    }
  }
  return total;
}

/** Per `<leagueId>#<opponent>` key: sum of clash TP on open round-card games. */
export function clashTacticalPointsByKeyForOpenGames(round, now = Date.now()) {
  const byKey = new Map();
  if (!round?.games) return byKey;
  const bets = normalizeBets(round.bets);
  for (const game of round.games) {
    if (!isGameOnRoundCard(game)) continue;
    const ca = bets[String(game.gameId)]?.clashAugments;
    if (!ca || typeof ca !== 'object') continue;
    for (const [k, v] of Object.entries(ca)) {
      if (v == null || v === '') continue;
      const c = getClashAugmentTacticalPointsCost(v);
      byKey.set(k, (byKey.get(k) ?? 0) + c);
    }
  }
  return byKey;
}

/** Highest clash TP spent on a single opponent among open games (for cap vs {@link CLASH_TACTICAL_POINTS_MAX_PER_CLASH}). */
export function maxClashTacticalSpendPerClashForOpenGames(round, now = Date.now()) {
  let m = 0;
  for (const v of clashTacticalPointsByKeyForOpenGames(round, now).values()) {
    m = Math.max(m, v);
  }
  return m;
}

/** Tactical points from match plays on all active round-card games (matches server required slate). */
export function totalTacticalPointsForOpenGamesBets(round, now = Date.now()) {
  if (!round?.games) return 0;
  const bets = normalizeBets(round.bets);
  let total = 0;
  let countedParlay = false;
  for (const game of round.games) {
    if (!isGameOnRoundCard(game)) continue;
    const aug = bets[String(game.gameId)]?.augment;
    if (!aug) continue;
    if (getAugmentBaseCode(aug) === PARLAY_AUGMENT_CODE) {
      if (countedParlay) continue;
      countedParlay = true;
    }
    total += getAugmentTacticalPointsCost(aug);
  }
  return total;
}

/**
 * Total tactical points after placing/removing a play (same rules as Betting handleAugmentPlace).
 */
export function totalTacticalPointsAfterPlacingAugment(round, targetGameId, augmentValue, now = Date.now()) {
  if (!round?.games) return 0;
  let bets = normalizeBets(round.bets);
  const tid = String(targetGameId);

  const existingCode = bets[tid]?.augment ? getAugmentBaseCode(bets[tid].augment) : null;
  const baseNew = augmentValue ? getAugmentBaseCode(augmentValue) : null;

  if (baseNew === 'm' || baseNew === BOUNTY_AUGMENT_CODE) {
    // Keep Sacrifice (d); replace any prior Multiplier or Bounty.
    bets = stripSacrificeFollowUpFromBets(bets);
  } else if (
    baseNew === 'd' ||
    existingCode === 'd' ||
    existingCode === 'm' ||
    existingCode === BOUNTY_AUGMENT_CODE
  ) {
    bets = stripSacrificeAugmentsFromBets(bets);
  } else if (baseNew === PARLAY_AUGMENT_CODE || existingCode === PARLAY_AUGMENT_CODE) {
    // Keep one other parlay when placing the second; clearing a parlay slot strips the pair below.
    if (existingCode === PARLAY_AUGMENT_CODE && !baseNew) {
      bets = stripParlayAugmentsFromBets(bets);
    }
  }

  if (baseNew && baseNew !== 'm' && baseNew !== BOUNTY_AUGMENT_CODE && baseNew !== PARLAY_AUGMENT_CODE) {
    for (const gid of Object.keys(bets)) {
      if (gid === tid) continue;
      if (bets[gid]?.augment && getAugmentBaseCode(bets[gid].augment) === baseNew) {
        const b = { ...bets[gid] };
        delete b.augment;
        bets[gid] = b;
      }
    }
  } else if (baseNew === PARLAY_AUGMENT_CODE) {
    let otherParlays = 0;
    for (const gid of Object.keys(bets)) {
      if (gid === tid) continue;
      if (bets[gid]?.augment && getAugmentBaseCode(bets[gid].augment) === PARLAY_AUGMENT_CODE) {
        otherParlays += 1;
      }
    }
    if (otherParlays >= PARLAY_MAX_USES) {
      bets = stripParlayAugmentsFromBets(bets);
    }
  }

  const existing = bets[tid] || { predictedHomeScore: 0, predictedAwayScore: 0 };
  const newBet = {
    ...existing,
    predictedHomeScore: existing.predictedHomeScore ?? 0,
    predictedAwayScore: existing.predictedAwayScore ?? 0
  };
  if (augmentValue) newBet.augment = augmentValue;
  else delete newBet.augment;
  bets[tid] = newBet;

  let total = 0;
  let countedParlay = false;
  for (const game of round.games) {
    if (!isGameOnRoundCard(game)) continue;
    const aug = bets[String(game.gameId)]?.augment;
    if (!aug) continue;
    if (getAugmentBaseCode(aug) === PARLAY_AUGMENT_CODE) {
      if (countedParlay) continue;
      countedParlay = true;
    }
    total += getAugmentTacticalPointsCost(aug);
  }
  return total;
}

/** Default match plays available to every player. */
export function getPlayerAugments() {
  return [...DEFAULT_AUGMENTS];
}
